import { Lightning } from '@lightningjs/sdk';
import { ButtonTemplateSpec } from 'components/common/buttons/Button';
import { getFontFaceFromStyle } from 'support/textUtils';
import ActionsList from 'components/common/ActionsList';
import { constants } from 'aliases';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { getImageTextureObj } from 'support/generalUtils';
import TextLabel from 'components/common/TextLabel';

export interface NotificationTemplateSpec
  extends Lightning.Component.TemplateSpec {
  backgroundColor: number;
  headerTextColor: number;
  messageTextColor: number;
  imageUrl: string;
  titleLabel: string;
  descLabel: string;
  borderColor: number;

  Content: {
    Image: Lightning.textures.ImageTexture;
    Heading: typeof TextLabel;
    Description: typeof TextLabel;
    Actions: typeof ActionsList;
  };
}

const WIDTH = 432;
const HEIGHT = 515;
const SMALL_HEIGHT = 260;
const OUTLINE = 4;

const ITEM_SPACING = 10;
const BUTTON_SPACING = 16;
const CONTENT_MARGIN = 16;

const HEADING_FONT_SIZE = 28;
const HEADING_HEIGHT = HEADING_FONT_SIZE + ITEM_SPACING;
const DESC_HEIGHT = 40;
const DESC_FONT_SIZE = 14;
const DESC_LINE_HEIGHT = 20;
const IMAGE_HEIGHT = 400;
const IMAGE_WIDTH = 225;

export default class Notification
  extends HoverableComponent<NotificationTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<NotificationTemplateSpec>
{
  private _Content = this.getByRef('Content')!;
  private _Actions = this._Content.getByRef('Actions')!;
  private _Image = this._Content.getByRef('Image')!;
  private _Heading = this._Content.getByRef('Heading')!;
  private _Description = this._Content.getByRef('Description')!;

  get announce() {
    const heading = this._Heading.text?.text;
    const desc = this._Description.text?.text;

    return [heading, desc];
  }

  set height(value: number) {
    this.patch({ h: value });
  }

  set imageUrl(url: string) {
    // hide the notification until the image fully loads
    this.updateAlpha(url ? constants.ui.invisible : 1);
    this.expandSize(!!url);
    if (url) {
      this._Image.patch(getImageTextureObj(url, IMAGE_HEIGHT, IMAGE_WIDTH));
    }
  }

  set backgroundColor(value: number) {
    this.patch({ color: value });
  }

  set headerTextColor(value: number) {
    const color = { text: { textColor: value } };
    this._Heading.patch(color);
  }

  set messageTextColor(value: number) {
    const color = { text: { textColor: value } };
    this._Description.patch(color);
  }

  set titleLabel(label: string) {
    this._Heading.patch({ text: { text: label } });
  }

  set descLabel(label: string) {
    this._Description.patch({ text: { text: label } });
  }

  set borderColor(color: number) {
    this.patch({ shader: { color } });
  }

  static override _template(): Lightning.Component.Template<NotificationTemplateSpec> {
    return {
      w: WIDTH,
      h: HEIGHT,
      rect: true,
      visible: false,
      zIndex: constants.ui.zIndex.notificationLayer,
      shader: {
        type: Lightning.shaders.Outline,
        width: OUTLINE,
      },
      Content: {
        x: CONTENT_MARGIN,
        y: CONTENT_MARGIN,
        w: WIDTH - CONTENT_MARGIN * 2,
        h: h => h - CONTENT_MARGIN * 2,
        shader: null,
        flex: {
          direction: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        },
        Image: {},
        Heading: {
          type: TextLabel,
          h: HEADING_HEIGHT,
          text: {
            fontSize: HEADING_FONT_SIZE,
            fontFace: getFontFaceFromStyle('bold'),
            textAlign: 'center',
            lineHeight: HEADING_HEIGHT,
            wordWrapWidth: WIDTH - 2 * CONTENT_MARGIN,
          },
        },
        Description: {
          type: TextLabel,
          h: DESC_HEIGHT,
          text: {
            wordWrap: true,
            wordWrapWidth: WIDTH - 2 * CONTENT_MARGIN,
            fontSize: DESC_FONT_SIZE,
            fontFace: getFontFaceFromStyle('regular'),
            maxLines: 2,
            lineHeight: DESC_LINE_HEIGHT,
            textAlign: 'center',
          },
        },
        Actions: {
          type: ActionsList,
          flexItem: {
            marginTop: ITEM_SPACING,
          },
          flex: {
            direction: 'column',
          },
          spacing: BUTTON_SPACING,
          zIndex: constants.ui.zIndex.notificationLayer + 1,
          signals: {},
          items: [],
        },
      },
    };
  }

  override _getFocused() {
    return this._Actions;
  }

  override _setup() {
    super._setup();
    this._Image.on('txLoaded', () => this.updateAlpha(1));
    this._Image.on('txError', () => {
      this.expandSize(false);
      this.updateAlpha(1);
    });
  }

  updateButtons(
    buttons: Lightning.Element.PatchTemplate<ButtonTemplateSpec>[],
  ) {
    if (!this._Actions || !buttons) return;

    const signals: Record<string, true> = {};
    buttons.forEach(({ action }) => {
      if (action) signals[action] = true;
    });

    this._Actions.patch({
      items: buttons,
      passSignals: signals,
    });
    this.signals = signals;
  }

  private updateAlpha(alpha: number) {
    this.patch({ alpha, shader: { alpha } });
  }

  private expandSize(hasImage: boolean) {
    this.height = hasImage ? HEIGHT : SMALL_HEIGHT;
    this._Heading.patch({
      flexItem: { marginTop: hasImage ? ITEM_SPACING : 0 },
    });
  }
}
